import { useTranslation } from 'react-i18next';
import { Box, ButtonBase } from '@mui/material';

/** Above every MUI layer (tooltip is 1500) so no dialog or drawer hides it. */
const MARKER_Z_INDEX = 1600;

/**
 * Permanent test-environment marker (#3312, epic #3309).
 *
 * A frame around the whole viewport plus a label hanging from its top edge.
 * Neither takes layout space, so no fixed header, drawer or bottom
 * navigation has to move — and it cannot be closed. Only the label catches
 * clicks: it opens the release page (#3316) when `onOpen` is given.
 */
export function TestEnvironmentMarker({ onOpen }: { onOpen?: () => void } = {}) {
  const { t } = useTranslation('common');

  return (
    <>
      <Box
        aria-hidden
        data-testid="test-environment-frame"
        sx={{
          position: 'fixed',
          inset: 0,
          border: 4,
          borderColor: 'warning.main',
          pointerEvents: 'none',
          zIndex: MARKER_Z_INDEX,
        }}
      />
      <Box
        {...(onOpen
          ? {
              component: ButtonBase,
              onClick: onOpen,
              'aria-label': t('testEnvironment.open'),
            }
          : { role: 'status' })}
        sx={{
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 'calc(100vw - 16px)',
          px: 1.5,
          py: 0.25,
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1.5,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          pointerEvents: onOpen ? 'auto' : 'none',
          cursor: onOpen ? 'pointer' : 'default',
          display: 'block',
          fontFamily: 'inherit',
          zIndex: MARKER_Z_INDEX,
        }}
      >
        {t('testEnvironment.label')}
        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, fontWeight: 400 }}>
          {' – '}
          {t('testEnvironment.description')}
        </Box>
      </Box>
    </>
  );
}
